import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import i18n from "i18next";
import { LuX } from "react-icons/lu";
import IdealStudio from "../../../../../core/editor/main";
import { isSlide, isBox, isContainedView, isSortableContainer } from "../../../../../common/utils";
import { ID_PREFIX_BOX, ID_PREFIX_SORTABLE_CONTAINER } from "../../../../../common/constants";
import { randomPositionGenerator } from "../../../clipboard/clipboard.utils";
import { createBox, instanceExists } from "../../../../../common/common_tools";
import multipleChoicePreview from "../../../../../images/assessment_previews/MultipleChoice.svg";
import multipleAnswerPreview from "../../../../../images/assessment_previews/MultipleAnswer.svg";
import freeResponsePreview from "../../../../../images/assessment_previews/FreeResponse.svg";
import ratingPreview from "../../../../../images/assessment_previews/Rating.svg";
import trueFalsePreview from "../../../../../images/assessment_previews/TrueFalse.svg";
import dragDropPreview from "../../../../../images/assessment_previews/DragDrop.svg";
import matchingPreview from "../../../../../images/assessment_previews/Matching.svg";
import fillInBlankPreview from "../../../../../images/assessment_previews/FillInBlank.svg";
import interactivePluginPreview from "../../../../../images/assessment_previews/InteractivePlugin.svg";
import AssetGrid from "../ui/AssetGrid";
import AssetCell from "../ui/AssetCell";
import PanelSearch from "../ui/PanelSearch";
import EmptyState from "../ui/EmptyState";
import BetaTag from "../../../common/BetaTag.jsx";
import "./_panel.scss";

// Keyed by plugin name; cards fall back to the plugin icon when a key is missing.
const PREVIEWS = {
  MultipleChoice: multipleChoicePreview,
  MultipleAnswer: multipleAnswerPreview,
  FreeResponse: freeResponsePreview,
  Rating: ratingPreview,
  TrueFalse: trueFalsePreview,
  DragDrop: dragDropPreview,
  Matching: matchingPreview,
  FillInBlank: fillInBlankPreview,
  InteractivePlugin: interactivePluginPreview,
};

const AssessmentItemsPanel = (props) => {
  const {
    boxes,
    boxSelected,
    navItems,
    navItemSelected,
    containedViews,
    containedViewSelected,
    onBoxAdded,
  } = props;

  const [buttons, setButtons] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const allConfigs = IdealStudio.Plugins.getPluginConfigs();
    const evaluationButtons = allConfigs.filter((cfg) => cfg.category === "evaluation");
    // InteractivePlugin lands at the end of the question list.
    //
    // InteractiveItem is filtered OUT: it is also an evaluation plugin, but
    // dropping an empty one onto a scene would be dropping a box with no
    // document and no data — there is nothing to author by hand. It reaches
    // the scene only through Soru Üretici's "İnteraktif" tab, which generates
    // the document and the data together.
    setButtons([
      ...evaluationButtons.filter(
        (c) => c.name !== "InteractivePlugin" && c.name !== "InteractiveItem"
      ),
      ...evaluationButtons.filter((c) => c.name === "InteractivePlugin"),
    ]);
  }, []);

  const clickAddBox = (event, name) => {
    const config = IdealStudio.Plugins.get(name).getConfig();
    if (!config) {
      return;
    }

    const page =
      containedViewSelected && containedViews[containedViewSelected]
        ? containedViews[containedViewSelected]
        : navItems[navItemSelected];

    if (!page) {
      return;
    }

    const cv =
      containedViewSelected &&
      containedViews[containedViewSelected] &&
      isContainedView(containedViewSelected);
    const cvslide = cv && isSlide(page.type);
    const cvdoc = cv && !isSlide(page.type);

    const isBoxSelected = boxSelected && boxes[boxSelected] && isBox(boxes[boxSelected]);

    if (isBoxSelected && isBox(boxes[boxSelected].parent) && config.isComplex) {
      // same depth limit behavior as PluginRibbon
      event && event.stopPropagation && event.stopPropagation();
      return;
    }

    if (config.limitToOneInstance && instanceExists(name)) {
      event && event.stopPropagation && event.stopPropagation();
      return;
    }

    const inASlide = (!cv && isSlide(page.type)) || cvslide;

    const SelectedNav = inASlide ? page.id : 0;

    const parentBox = inASlide ? 0 : cvdoc ? page.boxes[0] : page.boxes[0];

    let parent = isBoxSelected ? boxes[boxSelected].parent : inASlide ? SelectedNav : parentBox;

    let container = isBoxSelected
      ? boxes[boxSelected].container
      : inASlide
        ? 0
        : ID_PREFIX_SORTABLE_CONTAINER + Date.now();

    const isFullCanvas = config.initialWidth === "100%" && config.initialHeight === "100%";

    const position =
      container === 0
        ? {
            x: isFullCanvas ? 0 : randomPositionGenerator(10, 30),
            y: isFullCanvas ? 0 : randomPositionGenerator(10, 30),
            type: "absolute",
          }
        : undefined;

    const initialParams = {
      parent,
      container,
      name,
      position,
      id: ID_PREFIX_BOX + Date.now(),
      page: cv ? containedViewSelected : page.id || navItemSelected || 0,
    };

    if (!inASlide && isBoxSelected && isSortableContainer(container)) {
      const children = boxes[parent].sortableContainers[container].children;
      let newInd = children.indexOf(boxSelected) + 1;
      newInd = newInd === 0 ? 1 : newInd >= children.length ? children.length : newInd;
      initialParams.index = newInd;
      initialParams.col = 0;
      initialParams.row = 0;
    }

    createBox(initialParams, name, inASlide, onBoxAdded, boxes);

    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
  };

  const renderPreview = (button) => {
    const previewSrc = PREVIEWS[button.name];

    if (previewSrc) {
      return <img src={previewSrc} alt={i18n.t(button.name + ".PluginName")} />;
    }

    return (
      <span className="material-icons" style={{ fontSize: 40, color: "#94a3b8" }}>
        {button.icon}
      </span>
    );
  };

  const q = search.trim().toLowerCase();
  const filtered = q
    ? buttons.filter((b) => (i18n.t(b.name + ".PluginName") || "").toLowerCase().includes(q))
    : buttons;

  return (
    <div className="cmp-panel">
      <div className="cmp-panel__toolbar">
        <PanelSearch
          value={search}
          onChange={setSearch}
          onClear={() => setSearch("")}
          placeholder={i18n.t("search") || "Search"}
        />
        {props.onClose && (
          <button
            type="button"
            className="cmp-panel__close"
            onClick={props.onClose}
            title={i18n.t("close")}
          >
            <LuX size={15} />
          </button>
        )}
      </div>

      <div className="cmp-panel__scroll">
        {filtered.length === 0 ? (
          <EmptyState title={i18n.t("no_results")} hint={i18n.t("try_other_search")} />
        ) : (
          <AssetGrid columns={2} cellSize={120} gap={10}>
            {filtered.map((button) => {
              const label = i18n.t(button.name + ".PluginName");
              return (
                <AssetCell
                  key={button.name}
                  dragProps={{
                    className: "palette-item floatingEditorBox",
                    name: button.name,
                    "data-plugin": button.name,
                  }}
                  ratio={0.85}
                  title={label}
                  label={label}
                  onAdd={(e) => clickAddBox(e, button.name)}
                  badge={button.name === "InteractivePlugin" ? <BetaTag /> : undefined}
                >
                  {renderPreview(button)}
                </AssetCell>
              );
            })}
          </AssetGrid>
        )}
      </div>
    </div>
  );
};

AssessmentItemsPanel.propTypes = {
  boxes: PropTypes.object.isRequired,
  boxSelected: PropTypes.any,
  navItems: PropTypes.object.isRequired,
  navItemSelected: PropTypes.any.isRequired,
  containedViews: PropTypes.object.isRequired,
  containedViewSelected: PropTypes.any.isRequired,
  onBoxAdded: PropTypes.func.isRequired,
  onClose: PropTypes.func,
};

export default AssessmentItemsPanel;
